<template>
  <button
    type="button"
    class="health-record-card"
    :class="{ 'health-record-card--destaque': !!highlight }"
    @click="$emit('click')"
  >
    <span
      v-if="highlight"
      class="health-record-card__highlight"
      >{{ highlight }}</span
    >
    <span
      class="health-record-card__media"
      :class="{ 'health-record-card__media--image': !!image && !imageError }"
    >
      <img
        v-if="image && !imageError"
        :src="image"
        alt=""
        class="health-record-card__image"
        @error="imageError = true"
      />
      <OmtIcons
        v-else
        :name="icon"
        size="lg"
        color="var(--colors-light-primary-navy-blue)"
      />
    </span>

    <span class="health-record-card__body">
      <OmtTypography
        v-if="eyebrow"
        as="p2"
        class="health-record-card__eyebrow"
      >
        {{ eyebrow }}
      </OmtTypography>

      <OmtTypography
        as="h3-bold"
        class="health-record-card__title"
      >
        {{ title }}
      </OmtTypography>

      <OmtTypography
        v-if="description"
        as="p2"
        class="health-record-card__description"
      >
        {{ description }}
      </OmtTypography>

      <span
        v-if="statusLabel"
        class="health-record-card__status"
        :class="`health-record-card__status--${statusTone}`"
      >
        <span class="health-record-card__status-dot" />
        {{ statusLabel }}
      </span>
    </span>

    <OmtIcons
      name="chevron-right"
      size="sm"
      color="var(--colors-light-primary-gray1)"
      class="health-record-card__chevron"
    />
  </button>
</template>

<script>
/**
 * Cartao das listagens de saude (Analise de Pele e Insights de Saude), no padrao visual do
 * Hub de Saude.
 * @prop { String } title titulo principal
 * @prop { String } eyebrow linha pequena acima do titulo (ex.: data)
 * @prop { String } description previa do conteudo, limitada a 2 linhas
 * @prop { String } image miniatura; sem ela (ou se nao carregar), mostra o icone
 * @prop { String } icon icone usado quando nao ha miniatura
 * @prop { String } statusLabel texto do selo de status
 * @prop { String } statusTone "success", "warning" ou "neutral"
 * @prop { String } highlight selo de destaque (ex.: "Mais recente")
 */
export default {
  name: "HealthRecordCard",
  props: {
    title: { type: String, required: true },
    eyebrow: { type: String, default: "" },
    description: { type: String, default: "" },
    image: { type: String, default: "" },
    icon: { type: String, default: "heartbeat" },
    statusLabel: { type: String, default: "" },
    statusTone: {
      type: String,
      default: "neutral",
      validator: value => ["success", "warning", "neutral"].includes(value)
    },
    highlight: { type: String, default: "" }
  },
  data() {
    return {
      imageError: false
    };
  },
  watch: {
    image() {
      this.imageError = false;
    }
  }
};
</script>

<style scoped>
.health-record-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 12px 12px 12px;
  text-align: left;
  background: #ffffff;
  border: 1px solid var(--colors-light-secondary-divider);
  border-radius: 12px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.health-record-card:hover,
.health-record-card:focus-visible {
  border-color: var(--colors-light-primary-navy-blue);
  box-shadow: 0 4px 12px rgba(16, 24, 48, 0.08);
  outline: none;
}

.health-record-card__media {
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
  border-radius: 10px;
  background: var(--colors-light-primary-light-gray);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  align-self: flex-start;
}

.health-record-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.health-record-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* O selo fica apoiado na borda superior, metade para fora: nao disputa espaco com o titulo. */
.health-record-card--destaque {
  margin-top: 8px;
}

.health-record-card__eyebrow {
  color: var(--colors-light-primary-gray2) !important;
  margin: 0;
}

.health-record-card__highlight {
  position: absolute;
  top: -9px;
  right: 16px;
  white-space: nowrap;
  box-shadow: 0 0 0 2px var(--colors-light-primary-light-gray);
  background: var(--colors-light-primary-navy-blue);
  color: #ffffff;
  border-radius: 16px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
}

.health-record-card__title {
  color: var(--colors-light-primary-navy-blue) !important;
  margin: 0;
  overflow-wrap: anywhere;
}

.health-record-card__description {
  color: var(--colors-light-primary-gray1) !important;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.health-record-card__status {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 2px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}

.health-record-card__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.health-record-card__status--success {
  color: var(--colors-light-support-success);
  background: #eaf8f3;
}

.health-record-card__status--warning {
  color: #9a6700;
  background: #fff4dc;
}

.health-record-card__status--neutral {
  color: var(--colors-light-primary-gray1);
  background: var(--colors-light-primary-light-gray);
}

.health-record-card__chevron {
  flex-shrink: 0;
}
</style>
